import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { AxiosError } from "axios"

/**
 * Combines class names using clsx and tailwind-merge
 * This ensures Tailwind classes are properly merged without conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * ASP.NET validation error response structure
 */
interface AspNetValidationErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  errors?: Record<string, string[]>;
  traceId?: string;
}

/**
 * Parsed validation errors
 */
export interface ValidationErrors {
  fieldErrors: Record<string, string[]>;
  generalErrors: string[];
}

/**
 * Parse ASP.NET validation error response from Axios error
 *
 * Handles the standard ASP.NET validation error format:
 * {
 *   "errors": {
 *     "FieldName": ["Error message 1", "Error message 2"],
 *     "$.jsonPath": ["JSON parsing error"]
 *   }
 * }
 *
 * @param error - Axios error from failed request
 * @returns Parsed validation errors with field-specific and general errors
 */
export function parseValidationErrors(error: AxiosError): ValidationErrors {
  const fieldErrors: Record<string, string[]> = {};
  const generalErrors: string[] = [];

  // Check if this is a validation error response
  if (error.response?.status === 400 && error.response?.data) {
    const data = error.response.data as AspNetValidationErrorResponse;

    if (data.errors) {
      // Process each error field
      Object.entries(data.errors).forEach(([field, messages]) => {
        // Normalize field names:
        // - Convert JSON path errors ($.fieldName) to just fieldName
        // - Convert PascalCase to camelCase to match frontend field names
        // - Handle nested paths like "dto.fieldName" or just "dto"

        if (field === 'dto' || field.startsWith('dto.')) {
          // Generic DTO error - treat as general error
          generalErrors.push(...messages);
        } else if (field.startsWith('$.')) {
          // JSON parsing error - extract field name from path
          const fieldName = field.substring(2); // Remove "$."
          const camelCaseField = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
          fieldErrors[camelCaseField] = messages;
        } else {
          // Regular field validation error
          const camelCaseField = field.charAt(0).toLowerCase() + field.slice(1);
          fieldErrors[camelCaseField] = messages;
        }
      });
    }

    // If we have a title but no specific errors, use it as a general error
    if (data.title && Object.keys(fieldErrors).length === 0 && generalErrors.length === 0) {
      generalErrors.push(data.title);
    }
  }

  // If no validation errors found, use generic error message
  if (Object.keys(fieldErrors).length === 0 && generalErrors.length === 0) {
    const data = error.response?.data as { message?: string } | undefined;
    const message = data?.message || error.message || 'An error occurred';
    generalErrors.push(message);
  }

  return { fieldErrors, generalErrors };
}

/**
 * Format validation errors into a user-friendly message
 *
 * @param errors - Parsed validation errors
 * @returns Formatted error message string
 */
export function formatValidationErrors(errors: ValidationErrors): string {
  const messages: string[] = [];

  // Add general errors first
  messages.push(...errors.generalErrors);

  // Add field-specific errors
  Object.entries(errors.fieldErrors).forEach(([field, fieldMessages]) => {
    // Convert camelCase to Title Case for display
    const displayField = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
    fieldMessages.forEach((msg) => {
      messages.push(`${displayField}: ${msg}`);
    });
  });

  return messages.join('\n');
}

/**
 * Type guard to check if an error is an Axios error with response data
 */
interface ApiErrorResponse {
  message?: string;
  title?: string;
  errors?: Record<string, string[]>;
}

interface ApiError {
  response?: {
    data?: ApiErrorResponse;
    status?: number;
  };
  message?: string;
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('response' in error || 'message' in error)
  );
}

/**
 * Extract a user-friendly error message from an API error
 *
 * @param error - The error object (typically from a catch block)
 * @param defaultMessage - Default message if no specific error can be extracted
 * @returns A user-friendly error message string
 */
/**
 * Get today's date as a string in YYYY-MM-DD format
 * Useful for date input default values
 */
export function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0];
}

export function extractApiErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  if (!isApiError(error)) {
    return defaultMessage;
  }

  // Try to get message from response data
  const responseData = error.response?.data;
  if (responseData) {
    // Check for message field
    if (responseData.message) {
      return responseData.message;
    }
    // Check for title field (ASP.NET validation errors)
    if (responseData.title) {
      return responseData.title;
    }
    // Check for errors object (ASP.NET validation)
    if (responseData.errors) {
      const firstError = Object.values(responseData.errors)[0];
      if (firstError && firstError.length > 0) {
        return firstError[0];
      }
    }
  }

  // Fall back to error message
  if (error.message) {
    return error.message;
  }

  return defaultMessage;
}
